import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from '../lib/api'
import Icon from '../components/Icon'
import { SkeletonBlock } from '../components/Skeleton'
import { whenText } from '../lib/when'
import { amountText, categoryLabel, durationText, workedMsNow } from '../lib/remediation'
import WorkPill from '../components/WorkPill'
import { WorkActionButtons, WorkActionModal, DONE_TEXT } from '../components/WorkActions'
import SampleTag from '../components/SampleTag'
import { isSampleId } from '../lib/samples'

/**
 * My Ledgers — an accountant's own fixes: what's on them (Open) and what
 * they've finished (Done). Every action — Start, Pause, Resume, Mark done —
 * goes through a confirmation; clicking a card opens its detail page with
 * the full time record. Scoped server-side to whoever's signed in
 * (`remediation.controller.js#ownItemOf`).
 */

/** Whole days since an ISO timestamp — for the "oldest waiting" tile. */
function ageDays(iso) {
  if (!iso) return 0
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))
}

/** What's being worked on first, then what's parked, then what's untouched. */
const ORDER = { in_progress: 0, paused: 1, open: 2 }

const TABS = [
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
]

function ItemCard({ item, now, onOpen, onAction, busyWith }) {
  const amount = amountText(item.amount)
  const worked = workedMsNow(item, now)
  const s = item.status

  return (
    <article
      className={`rm-card rm-card-${s}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      title="Open the full record"
    >
      <div className="rm-card-head">
        <div className="rm-card-id">
          <span className="rm-code">{item.clientCode}</span>
          <SampleTag show={isSampleId(item._id)} />
          <span className="rm-cat">{categoryLabel(item.category)}</span>
          {item.originalAccountantEmail && (
            <span className="rm-covering">Covering for {item.originalAccountantName ?? item.originalAccountantEmail}</span>
          )}
        </div>
        <WorkPill status={s} />
      </div>

      <p className="rm-desc">{item.description}</p>

      {s === 'paused' && (
        <div className="rm-paused">
          Paused {whenText(item.pausedAt)}
          {item.pauseNote && <> — {item.pauseNote}</>}
        </div>
      )}

      <div className="rm-card-foot">
        <dl className="rm-facts">
          <div>
            <dt>Amount</dt>
            <dd className="rm-fact-amt">{amount ?? '—'}</dd>
          </div>
          <div>
            <dt>Raised</dt>
            <dd>{whenText(item.createdAt)}</dd>
          </div>
          <div>
            <dt>Time on it</dt>
            <dd className={s === 'in_progress' ? 'rm-worked' : undefined}>{worked > 0 ? durationText(worked) : '—'}</dd>
          </div>
          {s === 'confirmed' ? (
            <div>
              <dt>Done</dt>
              <dd>{whenText(item.confirmedAt)}</dd>
            </div>
          ) : (
            item.reference && (
              <div>
                <dt>Ref</dt>
                <dd>{item.reference}</dd>
              </div>
            )
          )}
        </dl>
        <div className="rm-card-right">
          <WorkActionButtons item={item} onAction={onAction} busyWith={busyWith} />
          <span className="rm-card-more" aria-hidden="true">
            Details ›
          </span>
        </div>
      </div>
    </article>
  )
}

export default function MyLedgers({ access }) {
  const navigate = useNavigate()
  const [tab, setTab] = useState('open')
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(null) // { item, action }
  const [toast, setToast] = useState(null)
  const [now, setNow] = useState(() => Date.now())

  const load = useCallback(async () => {
    try {
      const out = await api.listMyRemediationItems({ status: tab })
      setItems(out.items ?? [])
      setError(null)
    } catch (e) {
      setError(e.message)
      setItems([])
    }
  }, [tab])

  useEffect(() => {
    setItems(null)
    load()
  }, [load])

  // The "time on it" figures only move while something is in progress.
  const anyRunning = (items ?? []).some((i) => i.status === 'in_progress')
  useEffect(() => {
    if (!anyRunning) return undefined
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [anyRunning])

  const showToast = (text) => {
    setToast(text)
    setTimeout(() => setToast(null), 3500)
  }

  const sorted = useMemo(() => {
    const list = [...(items ?? [])]
    if (tab === 'done') return list.sort((a, b) => new Date(b.confirmedAt) - new Date(a.confirmedAt))
    return list.sort(
      (a, b) => (ORDER[a.status] ?? 3) - (ORDER[b.status] ?? 3) || new Date(a.createdAt) - new Date(b.createdAt)
    )
  }, [items, tab])

  const stats = useMemo(() => {
    const list = items ?? []
    return {
      count: list.length,
      inProgress: list.filter((i) => i.status === 'in_progress').length,
      paused: list.filter((i) => i.status === 'paused').length,
      notStarted: list.filter((i) => i.status === 'open').length,
      oldest: list.reduce((max, i) => Math.max(max, ageDays(i.createdAt)), 0),
      total: list.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
      worked: list.reduce((sum, i) => sum + workedMsNow(i, now), 0),
    }
  }, [items, now])

  const firstName = access?.accountant?.name?.split(' ')[0] ?? null
  const notAnAccountant = access && !access.accountant
  const loaded = items !== null

  return (
    <section className="screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">My Ledgers</h1>
          <div className="page-sub">
            {firstName ? `Hi ${firstName} — ` : ''}our-side ledger fixes on you. Start one when you pick it up, pause it
            if you're stuck, and mark it done once it's corrected and re-uploaded to Gati. Open any of them for its full
            time record.
          </div>
        </div>
      </div>

      {notAnAccountant ? (
        <div className="panel">
          <div className="empty">
            <div className="rm-empty-ico">
              <Icon name="user" size={20} />
            </div>
            <div className="rm-empty-t">You're not set up as an accountant yet</div>
            <div>Items are routed by category to the accounts team. Ask an admin to add you if you should be getting them.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="stats rm-stats">
            {tab === 'open' ? (
              <>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--amber)' }} />
                  <div className="stat-label">Open on you</div>
                  <div className="stat-value">{loaded ? stats.count : '—'}</div>
                  {loaded && stats.count > 0 && (
                    <div className="stat-meta">
                      {stats.inProgress} in progress · {stats.paused} paused · {stats.notStarted} not started
                    </div>
                  )}
                </div>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--red)' }} />
                  <div className="stat-label">Oldest waiting</div>
                  <div className="stat-value">{loaded && stats.count ? `${stats.oldest}d` : '—'}</div>
                </div>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--blue)' }} />
                  <div className="stat-label">Total value</div>
                  <div className="stat-value">{loaded && stats.count ? amountText(stats.total) : '—'}</div>
                </div>
              </>
            ) : (
              <>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--green)' }} />
                  <div className="stat-label">Finished</div>
                  <div className="stat-value">{loaded ? stats.count : '—'}</div>
                </div>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--blue)' }} />
                  <div className="stat-label">Time spent</div>
                  <div className="stat-value">{loaded && stats.worked ? durationText(stats.worked) : '—'}</div>
                </div>
                <div className="stat">
                  <div className="stat-accent" style={{ background: 'var(--ink-mute)' }} />
                  <div className="stat-label">Value fixed</div>
                  <div className="stat-value">{loaded && stats.count ? amountText(stats.total) : '—'}</div>
                </div>
              </>
            )}
          </div>

          <div className="rm-tabbar">
            <div className="seg" role="radiogroup" aria-label="Show">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={tab === t.value}
                  className={`seg-b${tab === t.value ? ' on' : ''}`}
                  onClick={() => setTab(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="note" style={{ marginBottom: 12 }}>{error}</div>}

          {!loaded && (
            <div className="rm-cards">
              {Array.from({ length: 3 }, (_, i) => (
                <SkeletonBlock key={i} height={132} style={{ borderRadius: 8 }} />
              ))}
            </div>
          )}

          {loaded && sorted.length === 0 && !error && (
            <div className="panel">
              <div className="empty">
                <div className="rm-empty-ico ok">
                  <Icon name="check" size={20} />
                </div>
                <div className="rm-empty-t">{tab === 'open' ? 'All clear' : 'Nothing finished yet'}</div>
                <div>{tab === 'open' ? 'Nothing is waiting on you right now.' : 'Fixes you mark done will show up here.'}</div>
              </div>
            </div>
          )}

          {sorted.length > 0 && (
            <div className="rm-cards">
              {sorted.map((item) => (
                <ItemCard
                  key={item._id}
                  item={item}
                  now={now}
                  onOpen={() => navigate(`/my-ledgers/${item._id}`)}
                  onAction={(i, action) => setPending({ item: i, action })}
                  busyWith={sorted.find((x) => x.status === 'in_progress') ?? null}
                />
              ))}
            </div>
          )}
        </>
      )}

      {pending && (
        <WorkActionModal
          item={pending.item}
          action={pending.action}
          onClose={() => setPending(null)}
          onDone={(i, action) => {
            setPending(null)
            showToast(DONE_TEXT[action](i))
            load()
          }}
        />
      )}

      {toast && (
        <div className="toast-wrap">
          <div className="toast toast-ok">{toast}</div>
        </div>
      )}
    </section>
  )
}
